import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import goToApp from '../utils/goToApp';
import './TeacherFreeTier.css';

/**
 * AssignmentLandingPage — /assignment/<share_token>
 *
 * Public landing for a notebook assignment (NotebookLM outcome loop).
 * Students arrive from a link their teacher shared. Flow:
 *   - already logged in            → straight into the tutor with the token
 *   - not logged in + class code   → first-name join (class-code flow), then tutor
 *   - not logged in, no class code → prompt to enter their class code first
 */

const AssignmentLandingPage = () => {
  const { shareToken } = useParams();
  const [assignment, setAssignment] = useState(null);
  const [error, setError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isAuthed, setIsAuthed] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    axios.get(`/api/notebooks/assignment-info/${shareToken}`)
      .then((r) => setAssignment(r.data.assignment))
      .catch(() => setError("We couldn't find that assignment. Double-check the link with your teacher."));
    axios.get('/api/auth/verify', { withCredentials: true })
      .then(() => setIsAuthed(true))
      .catch(() => setIsAuthed(false));
  }, [shareToken]);

  // Hard hop to the app host (see utils/goToApp) — soft navigate() left
  // students on the landing page after joining from a shared link.
  const startSession = () => goToApp(`/tutor?source=assignment&notebook_assignment=${shareToken}`);

  const joinAndStart = async (e) => {
    e.preventDefault();
    setError('');
    const cleanName = firstName.trim();
    if (!/^[A-Za-z][A-Za-z\-'\s]{0,30}$/.test(cleanName)) {
      setError('Enter your first name (letters only).');
      return;
    }
    setJoining(true);
    try {
      const r = await axios.post('/api/classroom/join', {
        code: assignment.class_code,
        first_name: cleanName,
      });
      if (r.data?.user) {
        localStorage.setItem('user_info', JSON.stringify(r.data.user));
        startSession();
      } else {
        setError('Joined, but something went wrong starting your session.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not join. Ask your teacher for help.');
    } finally {
      setJoining(false);
    }
  };

  if (error && !assignment) {
    return (
      <div className="teacher-onboarding-page">
        <div className="teacher-onboarding-card">
          <div className="teacher-step-badge">Assignment</div>
          <p className="teacher-step-body">{error}</p>
        </div>
      </div>
    );
  }
  if (!assignment) return null;

  return (
    <div className="teacher-onboarding-page">
      <div className="teacher-onboarding-card">
        <div className="teacher-step-badge">Assignment from {assignment.teacher_name}</div>
        <h2 className="teacher-step-title">{assignment.title}</h2>
        <p className="teacher-step-body">
          You'll talk through this with Mira — she has your class materials ready.
          {assignment.due_at ? ` Due ${new Date(assignment.due_at).toLocaleDateString()}.` : ''}
        </p>

        {isAuthed ? (
          <div className="teacher-step-actions" style={{ marginTop: 20 }}>
            <button className="teacher-btn-primary" onClick={startSession}>
              Start with Mira →
            </button>
          </div>
        ) : assignment.class_code ? (
          <form onSubmit={joinAndStart} style={{ marginTop: 20 }}>
            <input
              className="teacher-input"
              type="text"
              value={firstName}
              maxLength={30}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
              autoComplete="given-name"
              autoFocus
            />
            {error && <div className="teacher-inline-error">{error}</div>}
            <div className="teacher-step-actions" style={{ marginTop: 16 }}>
              <button type="submit" className="teacher-btn-primary" disabled={joining}>
                {joining ? 'Joining…' : 'Start with Mira →'}
              </button>
            </div>
          </form>
        ) : (
          <p className="teacher-step-hint" style={{ marginTop: 16 }}>
            Ask your teacher for your class code, join at{' '}
            <a href="/class-join">teachtappy.ai/class-join</a>, then open this link again.
          </p>
        )}
      </div>
    </div>
  );
};

export default AssignmentLandingPage;
